import { TOP_LAYER_VARS } from '@src/components/common/topLayerZ';
import { useAnchoredPopoverPlacement } from '@src/components/common/useAnchoredPopoverPlacement';
import { CLIP_PREVIEW_Z } from '@src/components/content-components/clipPreviewZ';
import SimplePencilIcon from '@src/svgs/SimplePencilIcon';
import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';

import type { AnchoredPopoverPlacement } from '@src/components/common/useAnchoredPopoverPlacement';

/** How long the hint stays up before it goes on its own. */
export const INLINE_EDIT_HINT_MS = 6000;

export interface InlineEditHintProps {
  /** The phrase span the reviewer just clicked; the hint sits under it. */
  anchorElement: HTMLElement;
  onDismiss: () => void;
}

/**
 * PLAT-11650 — shown ONCE per browser, under the first translated phrase a
 * reviewer single-clicks in Paragraph / Segment: a single click seeks, and the
 * field opens on a double-click, which nothing on screen says. Both usability
 * testers' instinct was one click, so the first one answers them. Same
 * footing as the red-line bubble (body-portaled, placed by measured height,
 * lifted above the review), quieter copy, and it leaves by itself.
 */
export const InlineEditHint: React.FC<InlineEditHintProps> = ({
  anchorElement,
  onDismiss,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const placement = useAnchoredPopoverPlacement(anchorElement, ref, {
    width: HINT_WIDTH,
    estimatedHeight: HINT_ESTIMATED_HEIGHT,
  });
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  useEffect(() => {
    const timer = setTimeout(() => onDismissRef.current(), INLINE_EDIT_HINT_MS);
    return () => clearTimeout(timer);
  }, []);

  return ReactDOM.createPortal(
    <Hint
      ref={ref}
      placement={placement}
      role="status"
      aria-live="polite"
      data-testid="inline-edit-hint"
      // A click on the hint itself is not a click away from the phrase.
      onMouseDown={e => e.stopPropagation()}
    >
      <HintGlyph aria-hidden="true">
        <SimplePencilIcon width="13" height="13" />
      </HintGlyph>
      <HintText>
        <HintTitle>Double-click a phrase to edit it in place</HintTitle>
        <HintMeta>Or hover the phrase and use its pencil</HintMeta>
      </HintText>
    </Hint>,
    document.body,
  );
};

const HINT_WIDTH = 280;
/** First-paint estimate only — the hook re-places against the real height. */
const HINT_ESTIMATED_HEIGHT = 48;

const Hint = styled.div.attrs(
  (props: { placement: AnchoredPopoverPlacement }) => props,
)`
  position: fixed;
  left: ${p => p.placement.left}px;
  top: ${p => p.placement.top}px;
  width: ${HINT_WIDTH}px;
  max-width: calc(100vw - 16px);
  z-index: var(
    ${TOP_LAYER_VARS.contextMenu},
    ${CLIP_PREVIEW_Z.translationEditorPopover}
  );
  /* The red-line bubble's shell (design system: Anchored Popovers), so the
     coaching hint and the edit bubble read as one family: glyph on the left,
     what it does on the first line, the other way in as a field-level hint. */
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 6px 10px;
  background: var(--popover);
  border: 1px solid var(--border);
  border-radius: var(--radius-input);
  box-shadow: var(--elevation-toast);
  line-height: 1.4;
  pointer-events: none;
`;

// The same glyph, in the same gold, as the hover pencil it points to.
const HintGlyph = styled.span`
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 4px;
  background: var(--surface-elevated);
  color: var(--accent);
`;

const HintText = styled.span`
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
`;

const HintTitle = styled.span`
  font-size: 12px;
  color: var(--foreground);
`;

const HintMeta = styled.span`
  font-size: 11px;
  color: var(--text-muted);
`;
